// Presentation metadata for the Antenna announcements composer (shot-43 / shot-37) — pure helpers, no React,
// so the composer + list + tests all read from one source of truth. The wire vocabularies are frozen in
// Cntrl.Contracts: announcement TYPE ∈ single|album|show|tour|onsale|note; STATUS ∈ draft|pub|retracted
// (note it is "pub", never "published"). The chip LABELS mirror shot-43 ("New single", "On sale", …).
import { Music, Disc3, Ticket, Route, Tag, FileText, type LucideIcon } from "lucide-react";

/** The 280-char body ceiling (CHECK; the server re-validates — this drives the 0/280 counter).*/
export const BODY_MAX = 280;

/** The frozen announcement types, in shot-43 chip order. */
export const ANNOUNCEMENT_TYPES = ["single", "album", "show", "tour", "onsale", "note"] as const;
export type AnnouncementType = (typeof ANNOUNCEMENT_TYPES)[number];

interface TypeMeta {
  /** The chip label as it reads in shot-43. */
  label: string;
  icon: LucideIcon;
}

const TYPE_META: Record<AnnouncementType, TypeMeta> = {
  single: { label: "New single", icon: Music },
  album: { label: "New album", icon: Disc3 },
  show: { label: "New show", icon: Ticket },
  tour: { label: "New tour", icon: Route },
  onsale: { label: "On sale", icon: Tag },
  note: { label: "Note", icon: FileText },
};

/** The chip label + icon for a type; falls back gracefully for an unknown/absent wire value. */
export function typeMeta(type: string | null | undefined): TypeMeta {
  return TYPE_META[(type ?? "") as AnnouncementType] ?? { label: type ?? "Post", icon: FileText };
}

/** The status badge label + tone. `pub` reads as "Published"; a retracted row is muted. */
export function statusMeta(
  status: string | null | undefined,
): { label: string; tone: "neutral" | "success" | "muted" } {
  switch (status) {
    case "pub":
      return { label: "Published", tone: "success" };
    case "retracted":
      return { label: "Retracted", tone: "muted" };
    case "draft":
    default:
      return { label: "Draft", tone: "neutral" };
  }
}

/** The audience-card sentinel for "Everyone" — an empty tier id means no gate (min_paid 0), F-06.*/
export const EVERYONE_TIER_ID = "";

/** A tier as rendered on an audience card (the wire tier, plus the synthetic "Everyone" option). */
export interface AudienceOption {
  /** The id sent as `audienceTierId` — `""` (Everyone) or a real tier id. */
  id: string;
  name: string;
  /** True for a paid tier (min_paid > 0) — the composer renders a lock, matching shot-43. */
  locked: boolean;
}

/** A settings tier as it arrives on the wire — every field is optional/nullable in the generated schema. */
interface WireTier {
  id?: string | null;
  name?: string | null;
  priceCents?: number | null;
}

/**
 * The audience options for the selector: a leading "Everyone" card followed by each club tier from
 * `GET settings`. Per-tier LIVE COUNTS are deliberately absent — band-metrics is A4 (F-06 degrade), so the
 * cards render without counts. The tier id (never a cents gate) is what the UI sends; the backend snapshots
 * `min_paid_cents` at publish.
 */
export function audienceOptions(tiers: readonly WireTier[] | null | undefined): AudienceOption[] {
  const clubTiers = (tiers ?? []).map((t) => ({
    id: t.id ?? "",
    name: t.name ?? "Members",
    locked: (t.priceCents ?? 0) > 0,
  }));
  return [{ id: EVERYONE_TIER_ID, name: "Everyone", locked: false }, ...clubTiers];
}

/** The audience label for a stored `audienceTierId` (null/"" ⇒ Everyone; else the tier name, else the raw id). */
export function audienceLabel(
  audienceTierId: string | null | undefined,
  tiers: readonly WireTier[] | null | undefined,
): string {
  if (!audienceTierId) return "Everyone";
  return (tiers ?? []).find((t) => t.id === audienceTierId)?.name ?? audienceTierId;
}

/** A short, locale-formatted date for a list row (matches shot-37's "7/10/2026" style). */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}
